/**
 * Returns the CSS string for the XMS Cookies consent banner.
 * @param {string} primaryColor
 * @param {string} theme - 'light' | 'dark'
 */
export function getStyles(primaryColor = '#1863dc', theme = 'light') {
  const isDark = theme === 'dark';

  const bg            = isDark ? '#1e1e2e' : '#ffffff';
  const bgAlt         = isDark ? '#16162a' : '#f7f8fc';
  const textPrimary   = isDark ? '#e8e8f0' : '#1a1a2e';
  const textSecondary = isDark ? '#9999b8' : '#5a5a7a';
  const borderColor   = isDark ? '#2e2e48' : '#e2e6f0';
  const borderLight   = isDark ? '#252540' : '#eef0f8';
  const shadowColor   = isDark ? 'rgba(0,0,0,0.45)' : 'rgba(15,25,70,0.11)';

  return `
/* =====================================================
   XMS Cookies — Consent Banner  v1.0
   ===================================================== */

#vc-cookie-consent *,
#vc-cookie-consent *::before,
#vc-cookie-consent *::after {
  box-sizing: border-box;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

/* ── Root container ──────────────────────────────────── */
#vc-cookie-consent {
  position: fixed;
  z-index: 2147483647;
  width: 100%;
  background: ${bg};
  color: ${textPrimary};
  box-shadow: 0 -3px 20px ${shadowColor};
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease;
}

/* Coloured accent line at top */
#vc-cookie-consent::before {
  content: '';
  display: block;
  height: 4px;
  background: linear-gradient(90deg, ${primaryColor}, ${primaryColor}bb);
  width: 100%;
}

#vc-cookie-consent.vc-position-bottom {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 48px);
  max-width: 845px;
  max-height: 79vh;
  border-radius: 6px;
  border: 1px solid #F4F4F4;
  box-shadow: 0 8px 40px ${shadowColor};
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

#vc-cookie-consent.vc-position-top    { top: 0; left: 0; box-shadow: 0 3px 20px ${shadowColor}; }
#vc-cookie-consent.vc-position-top::before { display: none; }

#vc-cookie-consent.vc-position-popup {
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 48px);
  max-width: 660px;
  max-height: 79vh;
  border-radius: 6px;
  border: 1px solid #F4F4F4;
  box-shadow: 0 12px 48px ${shadowColor};
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

#vc-cookie-consent.vc-slide-out { transform: translateY(108%); opacity: 0; }
#vc-cookie-consent.vc-position-bottom.vc-slide-out {
  transform: translateX(-50%) translateY(140%);
  opacity: 0;
}
#vc-cookie-consent.vc-position-top.vc-slide-out { transform: translateY(-108%); }
#vc-cookie-consent.vc-position-popup.vc-slide-out {
  transform: translateX(-50%) translateY(120%);
  opacity: 0;
}

/* ── Simple banner row ───────────────────────────────── */
.vc-banner-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 40px;
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

/* ── Brand ───────────────────────────────────────────── */
.vc-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.vc-brand-icon {
  width: auto;
  height: 36px;
  max-width: 170px;
  flex-shrink: 0;
}

.vc-brand-name {
  font-size: 14px;
  font-weight: 700;
  color: ${textPrimary};
  white-space: nowrap;
  letter-spacing: -0.01em;
}

/* ── Text area ───────────────────────────────────────── */
.vc-text-area {
  flex: 1;
  min-width: 220px;
}

.vc-title {
  font-size: 13.5px;
  font-weight: 700;
  color: ${textPrimary};
  margin-bottom: 4px;
}

.vc-description {
  font-size: 12.5px;
  color: ${textSecondary};
  line-height: 1.6;
}

.vc-description a {
  color: ${primaryColor};
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── Buttons (simple view) ───────────────────────────── */
.vc-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

/* Base button reset */
.vc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 27px;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  line-height: 24px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: unset;
  overflow-wrap: break-word;
  text-align: center;
  text-transform: none;
  text-shadow: none;
  box-shadow: none;
  transition: filter 0.18s, background 0.18s;
  -webkit-font-smoothing: antialiased;
}

.vc-btn:focus-visible {
  outline: 2px solid ${primaryColor};
  outline-offset: 2px;
}

/* Accept All — filled */
.vc-btn-primary {
  background: ${primaryColor};
  color: #ffffff;
  border-color: ${primaryColor};
}

.vc-btn-primary:hover { filter: brightness(0.88); }

/* Reject All / Save Prefs — outlined */
.vc-btn-secondary {
  background: ${bg};
  color: ${primaryColor};
  border-color: ${primaryColor};
}

.vc-btn-secondary:hover { background: ${primaryColor}10; }

/* Reject All text link in simple view */
.vc-btn-link {
  background: transparent;
  color: ${textSecondary};
  border-color: transparent;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.vc-btn-link:hover { color: ${textPrimary}; }

/* ── Logo + buttons row (below banner text) ─────────── */
.vc-logo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 14px;
}

.vc-logo-left {
  display: flex;
  align-items: center;
}

.vc-logo-left .vc-brand-icon {
  height: 44px;
  width: auto;
  max-width: none;
}

.vc-logo-row .vc-buttons {
  margin-left: auto;
}

/* ── Compliance badge (inside brand) ─────────────────── */
.vc-compliance-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 4px;
  background: ${primaryColor};
  color: #fff;
  margin-left: 7px;
  vertical-align: middle;
  line-height: 1.6;
}

/* ── Tab bar ──────────────────────────────────────────── */
.vc-tab-bar {
  display: flex;
  border-bottom: 1px solid ${borderColor};
  background: ${bgAlt};
  padding: 0 32px;
  overflow-x: auto;
  scrollbar-width: none;
  gap: 4px;
}

.vc-tab-bar::-webkit-scrollbar { display: none; }

.vc-tab {
  padding: 13px 20px;
  font-size: 13.5px;
  font-weight: 500;
  color: ${textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  letter-spacing: 0.005em;
}

.vc-tab:hover { color: ${textPrimary}; }

.vc-tab.active {
  color: ${primaryColor};
  border-bottom-color: ${primaryColor};
  font-weight: 700;
}

/* ── Scrollable panel ────────────────────────────────── */
.vc-panel {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 32px 8px;
  max-height: 320px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${borderColor} transparent;
}

.vc-panel::-webkit-scrollbar { width: 4px; }
.vc-panel::-webkit-scrollbar-thumb {
  background: ${borderColor};
  border-radius: 4px;
}

.vc-panel-content { display: none; }
.vc-panel-content.active { display: block; }

.vc-panel-intro {
  font-size: 12.5px;
  color: ${textSecondary};
  line-height: 1.7;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${borderLight};
}

/* ── Category rows ───────────────────────────────────── */
/* NOTE: HTML structure from _renderCategories():
   .vc-category > .vc-category-header + p.vc-category-desc
   .vc-category-header > span.vc-category-title + label.vc-toggle  */

.vc-category {
  padding: 14px 0;
  border-bottom: 1px solid ${borderLight};
}

.vc-category:last-child { border-bottom: none; }

.vc-category-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 7px;
}

/* category title + badge wrapper */
.vc-category-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13.5px;
  font-weight: 700;
  color: ${textPrimary};
  padding-top: 1px;
}

.vc-category-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
  letter-spacing: 0.01em;
}

.vc-badge-required {
  background: #e6f4ea;
  color: #1e7e34;
}

.vc-badge-optional {
  background: ${primaryColor}14;
  color: ${primaryColor};
}

.vc-category-desc {
  font-size: 12.5px;
  color: ${textSecondary};
  line-height: 1.65;
  padding-right: 60px; /* leave room for toggle */
}

/* ── Toggle switch ───────────────────────────────────── */
.vc-toggle {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
  margin-top: 0;
}

.vc-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.vc-toggle-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 26px;
  cursor: pointer;
  transition: background 0.22s;
}

.vc-toggle-slider::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.22s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.22);
}

.vc-toggle input:checked + .vc-toggle-slider {
  background: ${primaryColor};
}

.vc-toggle input:checked + .vc-toggle-slider::before {
  transform: translateX(20px);
}

.vc-toggle input:disabled + .vc-toggle-slider {
  background: #22c55e;
  cursor: default;
}

/* ── Cookie details table ────────────────────────────── */
.vc-cookie-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 10px;
  border: 1px solid ${borderColor};
  border-radius: 7px;
  overflow: hidden;
}

.vc-cookie-table th {
  text-align: left;
  padding: 8px 12px;
  background: ${bgAlt};
  color: ${textSecondary};
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${borderColor};
}

.vc-cookie-table td {
  padding: 8px 12px;
  border-top: 1px solid ${borderLight};
  color: ${textPrimary};
  vertical-align: top;
}

/* ── Detail action bar ───────────────────────────────── */
.vc-detail-actions {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 14px 32px 16px;
  border-top: 1px solid ${borderColor};
  background: ${bgAlt};
}

/* Inside the actions bar, buttons inherit the global .vc-btn size */
.vc-detail-actions .vc-btn {
  padding: 8px 27px;
}

/* ── About tab ───────────────────────────────────────── */
.vc-about-content {
  font-size: 13px;
  color: ${textSecondary};
  line-height: 1.7;
}

.vc-about-content h4 {
  font-size: 14px;
  font-weight: 700;
  color: ${textPrimary};
  margin-bottom: 12px;
}

.vc-about-content p { margin-bottom: 10px; }
.vc-about-content a { color: ${primaryColor}; text-decoration: underline; }

.vc-powered-by {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${borderLight};
  font-size: 11.5px;
  color: ${textSecondary}77;
}

.vc-powered-by a { color: ${primaryColor}; }

.vc-powered-by-footer {
  text-align: center;
  padding: 0 40px 7px;
  font-size: 11px;
  color: ${textSecondary}55;
}

.vc-powered-by-footer a {
  color: ${textSecondary}77;
  text-decoration: none;
}

.vc-powered-by-footer a:hover { text-decoration: underline; }

.vc-powered-by-inline {
  font-size: 11px;
  color: ${textSecondary}66;
  text-decoration: none;
  margin-right: auto;
  align-self: center;
}

.vc-powered-by-inline:hover { text-decoration: underline; }

/* ── Floating cookie button ──────────────────────────── */
#vc-cookie-settings-btn {
  position: fixed;
  bottom: 22px;
  left: 22px;
  z-index: 2147483646;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  background: transparent;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

#vc-cookie-settings-btn:hover {
  transform: scale(1.12) rotate(-10deg);
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}

#vc-cookie-settings-btn:focus-visible {
  outline: 3px solid ${primaryColor};
  outline-offset: 3px;
}

#vc-cookie-settings-btn svg,
#vc-cookie-settings-btn img {
  width: 56px;
  height: 56px;
  display: block;
  object-fit: contain;
}

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 720px) {
  #vc-cookie-consent.vc-position-bottom {
    bottom: 12px;
    width: calc(100% - 24px);
  }

  .vc-banner-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 20px 12px;
  }

  .vc-logo-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 0 16px 16px;
  }

  .vc-logo-row .vc-buttons {
    margin-left: 0;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .vc-logo-row .vc-buttons .vc-btn {
    width: 100%;
    justify-content: center;
    padding: 10px 16px;
  }

  .vc-powered-by-footer { padding: 0 20px 7px; }

  .vc-tab-bar { padding: 0 16px; gap: 0; }
  .vc-tab { padding: 11px 12px; font-size: 12.5px; }

  .vc-panel {
    max-height: 260px;
    padding: 16px 16px 8px;
    overflow-x: hidden;
  }

  .vc-panel-content { overflow-x: auto; }

  .vc-cookie-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-top: 10px;
  }

  .vc-cookie-table {
    min-width: 380px;
    margin-top: 0;
  }

  .vc-detail-actions {
    padding: 12px 16px 14px;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .vc-detail-actions .vc-btn {
    width: 100%;
    justify-content: center;
    padding: 10px 16px;
  }

  .vc-powered-by-inline {
    order: 99;
    text-align: center;
    margin-right: 0;
  }

  .vc-category-desc { padding-right: 0; }

  #vc-cookie-consent.vc-position-popup {
    bottom: 12px;
    left: 12px;
    width: calc(100% - 24px);
    transform: none;
  }

  #vc-cookie-consent.vc-position-popup.vc-slide-out {
    transform: translateY(120%);
  }

  #vc-cookie-settings-btn { width: 50px; height: 50px; }
  #vc-cookie-settings-btn svg, #vc-cookie-settings-btn img { width: 50px; height: 50px; }
}
`;
}
